// ============================================================================
// ui/color-picker.js — custom on-brand color picker popover
// ============================================================================
// Replaces the native <input type=color> (whose menu carried a DUPLICATE browser
// eyedropper). One clean, glassy, dark-navy popover with everything: a 2D shade
// area (saturation × value), a rainbow hue slider, a live preview, the WORKING
// plate-eyedropper, plus HEX and R/G/B inputs. Pure vanilla + CSS gradients (no
// canvas). Emits live hex via onChange as you drag/type (Ryan 2026-07-13).
//
//   const cp = createColorPicker({ onChange, onEyedrop, supportsEyedropper });
//   cp.setColor('#1E5AA8');           // seed without firing onChange
//   cp.open(anchorEl, mountEl);       // position near the swatch, mount into mountEl
//   cp.close();
// ============================================================================

import { h } from './dom.js';

// ── SAMPLING OUTSIDE THE BROWSER (Ryan 2026-08-02) ───────────────────────────
// "can it be modified to allow you to click a color outside of the browser window… a color from an
// image on your file explorer, or a color from a logo on another browser window".
//
// `window.EyeDropper` is the ONLY web API that can read a pixel the page does not own, so it is the
// native picker or nothing. Chromium desktop only (Chrome/Edge 95+); no Firefox, no Safari, no
// mobile — hence a real feature test rather than a UA sniff, and hence `ui/eyedropper.js` stays as
// the fallback rather than being replaced.
//
// ⚠ TWO THINGS THIS COSTS, both accepted deliberately:
//   1. It samples a RAW SCREEN PIXEL, which is exactly what `ui/eyedropper.js` was written to avoid:
//      `.wd-cchip::after` is a 34%-white `screen`-blend gradient over the top half of every swatch,
//      so a pixel taken near a chip's top edge reads much lighter than the chip's true colour. The
//      answer is that you do not need an eyedropper to pick a chip — you click the chip. The tool's
//      real targets are the plate and the artwork, and on both, the raw pixel IS the right answer
//      (the fallback already reads raw canvas pixels for the plate).
//   2. In exchange it FIXES the Add Logo tab: the preview there is an <img> (logo-step.js), and
//      `colorUnder()` has no <img> branch — it walks up to the nearest non-transparent background,
//      so sampling the buyer's own logo returned the PANEL's colour. That tab's own comment calls
//      that logo "the most likely thing they want to match".
const SUPPORTS_SCREEN_PICK = typeof window !== 'undefined'
  && typeof window.EyeDropper === 'function'
  && window.isSecureContext !== false;

// ── color math (hex ↔ rgb ↔ hsv) ─────────────────────────────────────────────
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const clamp255 = (n) => Math.max(0, Math.min(255, Math.round(n)));

function hexToRgb(hex) {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(String(hex || '').trim());
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
}
function rgbToHex(r, g, b) {
  const h2 = (n) => clamp255(n).toString(16).padStart(2, '0');
  return ('#' + h2(r) + h2(g) + h2(b)).toUpperCase();
}
function rgbToHsv(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let hue = 0;
  if (d) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue = hue * 60;
    if (hue < 0) hue += 360;
  }
  return { h: hue, s: max === 0 ? 0 : d / max, v: max };
}
function hsvToRgb(h, s, v) {
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

const EYE_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="m2 22 1-1h3l9-9"/><path d="M3 21v-3l9-9"/>' +
  '<path d="m15 6 3.4-3.4a2.1 2.1 0 0 1 3 3L18 9l.4.4a2.1 2.1 0 0 1 0 3 2.1 2.1 0 0 1-3 0l-6-6a2.1 2.1 0 0 1 0-3 2.1 2.1 0 0 1 3 0l.6.6Z"/></svg>';

/**
 * Pointer-drag helper: reports fractional (fx, fy) within `elm`. Moves are
 * rAF-THROTTLED (coalesced to one onMove per frame) so a 120 Hz pointer can't
 * flood the handler; `onEnd` fires once on release (used to commit the full-res
 * plate render, keeping the drag itself cheap).
 */
function onDrag(elm, onMove, onEnd) {
  let pending = null;
  let raf = null;
  const flush = () => { raf = null; if (pending) { onMove(pending.fx, pending.fy); pending = null; } };
  const compute = (e) => {
    const r = elm.getBoundingClientRect();
    pending = { fx: clamp01((e.clientX - r.left) / r.width), fy: clamp01((e.clientY - r.top) / r.height) };
    if (!raf) raf = requestAnimationFrame(flush);
  };
  elm.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    try { elm.setPointerCapture(e.pointerId); } catch (_) { /* older engine */ }
    compute(e);
    const move = (ev) => compute(ev);
    const up = () => {
      elm.removeEventListener('pointermove', move);
      elm.removeEventListener('pointerup', up);
      elm.removeEventListener('pointercancel', up);
      if (raf) { cancelAnimationFrame(raf); raf = null; }
      if (pending) { onMove(pending.fx, pending.fy); pending = null; } // flush the final position
      if (typeof onEnd === 'function') onEnd();
    };
    elm.addEventListener('pointermove', move);
    elm.addEventListener('pointerup', up);
    elm.addEventListener('pointercancel', up);
  });
}

/**
 * @param {object} opts
 * @param {(hex:string)=>void} opts.onChange           - live hex as the user edits.
 * @param {()=>Promise<string|null>} [opts.onEyedrop]  - IN-PAGE eyedropper flow → resolves a hex (or
 *        null). Used only where `window.EyeDropper` is absent (Firefox / Safari / mobile); on
 *        Chromium desktop the native screen-wide picker runs instead. See SUPPORTS_SCREEN_PICK.
 * @param {()=>void} [opts.onScreenPickStart] - fired (never awaited) just before the native picker
 *        opens, so the host can square the 3D plate up behind it. MUST NOT be awaited: `open()`
 *        needs the click's user activation, and an await would spend it.
 * @param {()=>void} [opts.onScreenPickEnd]   - fired when the native picker resolves OR is dismissed.
 * @param {(slotKey:string, hex:string)=>void} [opts.onAssign] - "Apply to" footer (assign mode): the
 *        user dialed a color, then tapped a slot → apply `hex` to `slotKey` (see open()'s `assign` opt).
 * @param {boolean} [opts.supportsEyedropper]
 * @returns {{ el:HTMLElement, open:(anchor:HTMLElement, mount:HTMLElement, opts?:object)=>void, close:()=>void, setColor:(hex:string)=>void, isOpen:()=>boolean }}
 */
export function createColorPicker({ onChange, onEyedrop, onScreenPickStart, onScreenPickEnd, onAssign, onClose, supportsEyedropper } = {}) {
  let hsv = { h: 0, s: 0, v: 0 };
  let opened = false;
  let anchorEl = null;
  /* CONFIRM IS GATED UNTIL SOMETHING ACTUALLY CHANGES (Ryan 2026-08-05). Pressing it unchanged was
     already a silent no-op — `recolorEverywhere` returns false when `from === to` — so the button was
     lying about being actionable. Greying it makes that state visible instead of discovered. */
  let openedHex = null;
  let confirmBtn = null;
  /* ── THE COLOUR IS NOT THE ONLY THING THIS POPOVER CAN CHANGE (Ryan 2026-08-08) ─────────────────
   * *"it's gated (grayed out), waiting for a color change. It should also un-gray if you adjust the
   * line thickness."* The gate was written when a colour was the only edit on offer, and the outline
   * editor has since grown a thickness dial in its `extra` slot. That dial belongs to the HOST
   * (design-panel builds it and owns what it writes), so this component cannot observe it and has to
   * be told when it moves — `syncExtra`.
   *
   * ⛔ A COMPARISON, NOT A LATCH, and the first version got that wrong. Ryan: *"if the text outline
   * is 2, I go to edit it and increase it to 2.5, the update button should ungray. But then if I
   * change it back to 2, it should gray again."* A `dirty = true` flag can only ever open the gate;
   * it remembers that something happened rather than describing what the design is now. So the host
   * hands over a READER (`opts.extraValue`), this snapshots it at open beside `openedHex`, and the
   * two are compared the same way — walk the dial back to where it started and the button closes
   * again, exactly as walking the colour back already did.
   *   (Same shape as the outline-colour bug earlier the same day: a guard that records history
   * instead of deriving from state is a guard that answers differently depending on the route.) */
  let activeExtraValue = null;   // () => comparable, supplied per open by the host
  let openedExtra = null;        // its value at open / at the last commit
  const readExtra = () => (typeof activeExtraValue === 'function' ? activeExtraValue() : null);
  const syncConfirmEnabled = () => {
    if (!confirmBtn) return;
    const hexMoved = openedHex == null || currentHex() !== openedHex;
    const changed = hexMoved || readExtra() !== openedExtra;
    confirmBtn.disabled = !changed;
    confirmBtn.classList.toggle('is-disabled', !changed);
  };
  /** Host-facing: "my `extra` control moved — re-ask whether anything differs from the baseline." */
  const syncExtra = () => syncConfirmEnabled();

  // --- DOM ---
  const svCursor = h('div', { class: 'wd-cp__sv-cursor' });
  const svArea = h('div', { class: 'wd-cp__sv' },
    h('div', { class: 'wd-cp__sv-sat' }),
    h('div', { class: 'wd-cp__sv-val' }),
    svCursor,
  );
  const hueCursor = h('div', { class: 'wd-cp__hue-cursor' });
  const hueBar = h('div', { class: 'wd-cp__hue' }, hueCursor);
  const preview = h('div', { class: 'wd-cp__preview', 'aria-hidden': 'true' });

  // ONE button, two reaches. Same glyph and same spot either way — which picker runs is a property
  // of the browser, not a choice the buyer should have to make, so only the wording differs.
  const eyeHint = SUPPORTS_SCREEN_PICK
    ? 'Pick a color from anywhere on your screen — even outside the browser'
    : 'Pick a color from this page';
  const eyeBtn = supportsEyedropper && (SUPPORTS_SCREEN_PICK || typeof onEyedrop === 'function')
    ? h('button', { type: 'button', class: 'wd-cp__eye', title: eyeHint, 'aria-label': `Eyedropper — ${eyeHint}` },
        h('span', { 'aria-hidden': 'true', html: EYE_ICON }))
    : null;

  const hexInput = h('input', { type: 'text', class: 'wd-cp__hex', spellcheck: 'false', 'aria-label': 'Hex color', maxlength: '7' });
  const mkNum = (label) => {
    const inp = h('input', { type: 'number', min: '0', max: '255', class: 'wd-cp__num', 'aria-label': `${label} 0–255` });
    return { wrap: h('label', { class: 'wd-cp__field' }, h('span', {}, label), inp), inp };
  };
  const R = mkNum('R'), G = mkNum('G'), B = mkNum('B');

  // "Apply to" footer — only shown in ASSIGN mode (open() with an `assign` list). The
  // user dials any color / eyedrops, then taps a slot button to place it. Empty +
  // hidden otherwise (a direct role-edit open commits live to its own role instead).
  const assignRow = h('div', { class: 'wd-cp__assign', hidden: true });

  // TARGET tabs (Ryan 2026-07-24) — for a row where the row-level ✎ could mean more than one thing.
  // The outline rows are the only place this happens: several outlines can be on at once, so the tabs
  // let you say WHICH ring you're editing (Inner / Middle / Outer / Added) without leaving the picker.
  // Rendered only when there's more than one target, so every single-target open is untouched.
  const targetRow = h('div', { class: 'wd-cp__targets' });
  /* The strip's caption. It exists because the SAME control means two different things depending on
     what opened it, and unlabelled they are indistinguishable: on the Add Logo tab these swatches are
     "choose a colour to edit", while the row below them ("Use a color from your design") swaps a
     colour IN. Ryan hit exactly that collision on the plate row (2026-08-05): *"they mean a different
     thing to 'Use a color from your design' — they in fact mean 'Choose a color to edit'."*
       Only the caller knows which sentence is true, so the caller supplies it. The outline rows pass
     nothing: their tabs are Inner / Middle / Outer, named slots that explain themselves. */
  const targetLabel = h('div', { class: 'wd-cp__rowlabel' });
  const targetBlock = h('div', { class: 'wd-cp__targetblock', hidden: true }, targetLabel, targetRow);
  let targetButtons = [];

  // EXTRA control slot (Ryan 2026-07-25) — the host may hang one control here, directly under the
  // target tabs. Built for outline THICKNESS: it has to act on whichever ring the lit tab names, so
  // it belongs beside the tabs rather than out in the row, where there is no target to speak of.
  // The picker stays ignorant of what it is — the host owns the node and re-fills it on a tab change
  // — so this never becomes a second place that knows about steppers.
  const extraRow = h('div', { class: 'wd-cp__extra', hidden: true });

  /* SWAP row (Ryan 2026-08-05) — one click to put a colour the design ALREADY uses into the slot
     being edited, instead of dialling it or hunting a hex.
       *"when these anomalies/outlier events happen where I'm on the training bumper and I want to
     change the wrong outline color from the almost black to the blue, there's no easy way to swap in
     one default color for another… perhaps there can be a button for this on the color pop-up when
     you click edit for a color swatch."*
       The pool is the logo's palette PLUS the saved swatches — deliberately the same pool
     `assignOutlineColors` picks from, so this row offers exactly what the auto-derivation would have
     chosen. A click behaves as though you had dialled that colour and pressed the confirm button:
     same commit, same announcement, no second code path to keep in step. */
  const swapRow = h('div', { class: 'wd-cp__swap' });
  /* ⚠ EVERY ROW IN THIS POPOVER NOW SAYS WHAT IT IS (Ryan 2026-08-05). The popover had grown three
     stacked sections — target tabs, the `extra` slot, these chips — and NONE of them carried a
     heading, while every row OUTSIDE it does ("Plate", "Text", "Text outlines"). So this was the one
     surface in the panel where you had to infer a control from its shape, and it misdirected its own
     author: Ryan opened the outline editor looking for the colour swapper and read the unlabelled
     thickness stepper at the top as *"nothing to do with adjusting a new default color"*.
       The `extra` slot's label belongs to the HOST, not here — the picker stays ignorant of what that
     control is, which is the rule that keeps it from becoming a second place that knows about
     steppers. It owns this row, so it labels this row. */
  const swapBlock = h('div', { class: 'wd-cp__swapblock', hidden: true },
    h('div', { class: 'wd-cp__rowlabel' }, 'Use a color from your design'),
    swapRow);

  /* ── THE SHEET'S GRAB LINE, AND WHAT IT PROMISES (Ryan 2026-08-08) ──────────────────────────────
   * *"in the same way you can drag up and down the normal slide-up menu, you should be able to drag
   * down the color slide-up menu when it's opened… it needs that short gray line across the header
   * like the main slide-up menu has, and you should be able to click and drag down to close it."*
   *
   * On a phone this card IS a bottom sheet — same footprint, same bottom edge — and it was the one
   * sheet in the tool with no way out by gesture. The line is the affordance and the drag is the
   * behaviour; shipping the line without the drag would be a lie, which is why they are one change.
   *   Its own element rather than a `::before` on the card: the card SCROLLS now (see the max-height
   * in `position()`), and a pseudo-element on the scroll box would slide away with the content. This
   * one is sticky at the top of that box, so the handle is always where the thumb reaches for it.
   *   Desktop hides it in CSS — there the picker is a docked popover with a Close button. */
  const grabber = h('div', { class: 'wd-cp__grab', 'aria-hidden': 'true' });
  /* ── AND AN EXIT THAT IS ALWAYS THERE (Ryan 2026-08-09) ─────────────────────────────────────────
   * The sheet stopped light-dismissing on the same ask (see `onDocDown`), which is what he wanted —
   * and it leaves the ways out as the confirm button, this handle, and Escape. The confirm button
   * GREYS whenever nothing has changed, Escape wants a keyboard, and a gesture is a thing you have
   * to already know. That combination is a card you can open and, on the wrong path, not obviously
   * leave — the exact shape of `never-enter-a-screen-whose-exit-is-conditional`.
   *   So the header carries a plain ✕ as well. Sheet mode only: desktop still has its light-dismiss,
   * its Escape and, in keep-open mode, a Close button in the footer. */
  const closeX = h('button', { type: 'button', class: 'wd-cp__x', 'aria-label': 'Close color picker' },
    h('span', { 'aria-hidden': 'true' }, '✕'));
  /* `close` is a hoisted function declaration further down, so this reads it at click time. */
  closeX.addEventListener('click', (e) => { e.stopPropagation(); close(); });
  /* One sticky row for both, rather than two sticky siblings. The card scrolls, so the handle and
     the exit have to stay put together — and the row is what lets the card's own top padding move
     ONTO it (see the stylesheet), so scrolled content passes under a solid header instead of
     appearing in the gutter above a floating line. */
  const sheetHead = h('div', { class: 'wd-cp__sheethead' }, grabber, closeX);
  const el = h('div', { class: 'wd-cp', role: 'dialog', 'aria-label': 'Color picker' },
    sheetHead,
    targetBlock,
    extraRow,
    swapBlock,
    svArea,
    hueBar,
    /* ── HEX AND RGB SHARE A LINE ON THE PHONE (Ryan 2026-08-08) ────────────────────────────────
     * *"is there any way to get the HEX and RGB on the same row on mobile? Decrease the amount of
     * potential scrolling required on these color popups?"*
     *
     * They were siblings in the card's flex COLUMN, so nothing could put them side by side without a
     * box to share. This wrapper is that box and nothing else: it is `display: contents` by default,
     * so on desktop the two rows are still the card's own children and every existing rule applies
     * unchanged — the phone stylesheet is the only place it becomes a flex row.
     *   ⚠ `display: contents` is doing real work here rather than being tidy. Without it this wrapper
     * would be a new flex item in the column, and `.wd-cp--sheet > *`'s `flex-shrink: 0` (added the
     * same day for the hue bar) would apply to the WRAPPER while the rows inside it went back to
     * being shrinkable — quietly re-opening the zero-height bug one level down. */
    h('div', { class: 'wd-cp__io' },
      h('div', { class: 'wd-cp__row' },
        preview,
        ...(eyeBtn ? [eyeBtn] : []),
        hexInput,
      ),
      h('div', { class: 'wd-cp__rgb' }, R.wrap, G.wrap, B.wrap),
    ),
    assignRow,
  );

  // --- sync the UI from `hsv`; `focusEl` is skipped so it isn't clobbered mid-type ---
  function currentHex() { const { r, g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v); return rgbToHex(r, g, b); }
  function paint(focusEl) {
    const hex = currentHex();
    const pure = hsvToRgb(hsv.h, 1, 1);
    svArea.style.background = rgbToHex(pure.r, pure.g, pure.b);
    svCursor.style.left = hsv.s * 100 + '%';
    svCursor.style.top = (1 - hsv.v) * 100 + '%';
    svCursor.style.background = hex;
    hueCursor.style.left = (hsv.h / 360) * 100 + '%';
    preview.style.background = hex;
    if (focusEl !== hexInput) hexInput.value = hex;
    const { r, g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v);
    if (focusEl !== R.inp) R.inp.value = String(clamp255(r));
    if (focusEl !== G.inp) G.inp.value = String(clamp255(g));
    if (focusEl !== B.inp) B.inp.value = String(clamp255(b));
  }
  // Assign-footer buttons that carry a live `disabled(hex)` predicate (e.g. "New" grays
  // out when the previewed color already exists in the lineup — Ryan 2026-07-20). Rebuilt
  // on each open(); re-evaluated on every color change so the state tracks the preview.
  let assignButtons = [];
  // Confirm mode ("Add color") — set by open({confirm}). A single primary button commits
  // the crafted color; a light-dismiss / Escape cancels (fires onCancel). Cleared the
  // moment it's confirmed OR the picker closes, so close() knows whether to cancel.
  let activeConfirm = null;
  function updateAssignStates() {
    if (!assignButtons.length) return;
    const hex = currentHex();
    assignButtons.forEach(({ btn, disabled }) => {
      if (!disabled) return;
      const off = !!disabled(hex);
      btn.disabled = off;
      btn.classList.toggle('is-disabled', off);
    });
  }
  // `live` (default true) = mid-interaction → the host does a cheap/low-res plate
  // refresh; `live:false` = COMMIT (drag release, field blur, eyedrop) → the host
  // does the full-res render + history. Keeps dragging smooth (Ryan 2026-07-13).
  function emit(focusEl, live = true) { paint(focusEl); updateAssignStates(); syncConfirmEnabled(); if (typeof onChange === 'function') onChange(currentHex(), live); }

  // --- interactions ---
  onDrag(svArea, (fx, fy) => { hsv.s = fx; hsv.v = 1 - fy; emit(null, true); }, () => emit(null, false));
  onDrag(hueBar, (fx) => { hsv.h = fx * 360; emit(null, true); }, () => emit(null, false));

  hexInput.addEventListener('input', () => {
    const m = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(hexInput.value.trim());
    if (!m) return;
    let hx = m[1];
    if (hx.length === 3) hx = hx[0] + hx[0] + hx[1] + hx[1] + hx[2] + hx[2];
    const { r, g, b } = hexToRgb('#' + hx);
    hsv = rgbToHsv(r, g, b);
    emit(hexInput, true);
  });
  hexInput.addEventListener('change', () => emit(hexInput, false)); // blur / Enter → commit
  const onRgb = (focusEl) => () => {
    const r = clamp255(+R.inp.value || 0), g = clamp255(+G.inp.value || 0), b = clamp255(+B.inp.value || 0);
    hsv = rgbToHsv(r, g, b);
    emit(focusEl, true);
  };
  [R, G, B].forEach(({ inp }) => {
    inp.addEventListener('input', onRgb(inp));
    inp.addEventListener('change', () => emit(inp, false)); // blur / Enter → commit
  });

  if (eyeBtn) {
    /** A sampled hex lands exactly where a typed one does — a COMMIT (live:false), so it is undoable
     *  and both open modes (direct-edit and "+ Add") behave as they always have. */
    const applyPicked = (hex) => {
      if (!hex || !/^#?[\da-f]{6}$/i.test(String(hex).trim())) return;
      const { r, g, b } = hexToRgb(hex);
      hsv = rgbToHsv(r, g, b);
      emit(null, false);
    };

    // The in-page fallback: today's behaviour, unchanged, for browsers with no EyeDropper.
    const pickInPage = async () => {
      if (typeof onEyedrop !== 'function') return;
      // HIDE the picker while sampling so it can't cover the thing you want to pick (Ryan 2026-07-23).
      // visibility:hidden also drops it from elementFromPoint, so a colour behind it is reachable.
      const prevVis = el.style.visibility;
      el.style.visibility = 'hidden';
      let hex = null;
      try { hex = await onEyedrop(); } finally { el.style.visibility = prevVis; } // re-show with the pick
      applyPicked(hex);
    };

    // The native screen-wide picker. Chrome takes over the whole desktop with its own magnifier, so
    // the page's own overlay is irrelevant once this opens — the two can never both be armed, which
    // is why this is a branch and not a second button.
    const pickScreen = () => {
      let dropper;
      try { dropper = new window.EyeDropper(); } catch { return pickInPage(); }
      // ⛔ NOTHING MAY BE AWAITED BETWEEN THE CLICK AND open(). It requires transient user activation;
      // an await hands the turn back to the event loop and the call throws. So the host's square-up is
      // FIRED and not awaited — its tween runs behind the magnifier and the plate settles flat while
      // you aim, which is all the awaiting ever bought.
      try { if (typeof onScreenPickStart === 'function') onScreenPickStart(); } catch { /* never block the pick */ }
      // Deliberately NOT hiding the picker: it docks in the gap beside the plate, the thing being
      // sampled is off-window, and a hide/show would just flash. The magnifier dims the screen anyway.
      dropper.open()
        .then((res) => applyPicked(res && res.sRGBHex))
        .catch(() => { /* Escape / dismissed rejects with AbortError — a cancel, not a failure */ })
        .finally(() => { try { if (typeof onScreenPickEnd === 'function') onScreenPickEnd(); } catch { /* ignore */ } });
    };

    eyeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (SUPPORTS_SCREEN_PICK) pickScreen(); else pickInPage();
    });
  }

  // --- open / close + positioning + light-dismiss ---
  // DOCKED mode (Ryan 2026-07-23): on desktop the picker pins to the RIGHT edge of the slide-out
  // pane and fills the white gap out to the plate, instead of floating over the swatch. This keeps
  // every swatch in the pane visible (so you can see which one you're editing + sample the others)
  // and gives the mouse a crisp "inside vs outside" boundary for the auto-arm eyedropper. The svg
  // carries the --wd-plate-x transform, so its rect.left IS the visible plate's left edge.
  // Docked-mode geometry (Ryan 2026-07-24). The plate-side clearance is deliberately LARGER than the
  // pane-side one: when the gap is narrower than DOCK_MAX_W the picker's width IS the gap, so its
  // right edge lands exactly PLATE_GAP from the 3D model — at the old symmetric 12px it crowded the
  // plate. Widening only that side pushes the picker off the model without moving it away from the
  // pane it belongs to. MAX_W also came down a touch so it reads a fraction narrower on wide screens.
  const DOCK_PANE_GAP = 12;
  const DOCK_PLATE_GAP = 28;
  const DOCK_MIN_W = 236;
  const DOCK_MAX_W = 340;
  function dockRect() {
    if (window.innerWidth <= 760) return null; // phone/narrow → fall back to anchor-float
    const pane = document.querySelector('.weight-designer .ds-side-panel');
    const plate = document.querySelector('.wd-canvas-stage svg, .wd-canvas-stage canvas');
    if (!pane || !plate) return null;
    const pr = pane.getBoundingClientRect();
    const gapLeft = pr.right + DOCK_PANE_GAP;
    const gapRight = plate.getBoundingClientRect().left - DOCK_PLATE_GAP;
    if (gapRight - gapLeft < 150) return null; // gap too tight to dock cleanly
    return { left: gapLeft, width: Math.max(DOCK_MIN_W, Math.min(DOCK_MAX_W, gapRight - gapLeft)) };
  }
  /**
   * THE vertical position of the picker on desktop — one fixed spot ON THE TOOL, every time (Ryan
   * 2026-07-26). Applies to the Add Logo tab and Customize Design alike: both tabs' pickers run
   * through this function.
   *
   * It sits a gap up from the bottom of the CANVAS FRAME, and nothing else gets a vote. Not the
   * swatch that opened it, not the scroll position, and — the part that took two goes — not whether
   * the result is fully on screen. Ryan, on the first attempt: "color pop-up moves higher so the
   * bottom is ALWAYS visible on my display whenever it pops-up, as opposed to being locked into the
   * same absolute position every time which is what I want."
   *
   * So the viewport clamps are deliberately GONE. Keeping the picker on screen is exactly what makes
   * it land somewhere new each time — clamping IS the movement. Scrolled so the bottom of the
   * designer is past the fold, the picker's own bottom goes with it, and scrolling down brings it
   * back into view at the place it has always been. Predictable beats always-visible here: the eye
   * learns one location, and a picker that is occasionally half off-screen is a scroll away, while
   * one that teleports is a hunt every time.
   *
   * The picker is `position: fixed`, so this reads the frame's CURRENT viewport rect and is computed
   * once per open. It never has to track a scroll, because scrolling dismisses the picker outright
   * (`onReflow`) — so "the same place on the tool" and "the same place on screen for a given scroll
   * position" are the same statement here.
   *
   * DOCK_PANE_GAP is the same 12px inset it holds from the pane on its left, so the margin reads as
   * one consistent frame on both sides.
   */
  const stableTop = (hgt) => {
    const frame = document.querySelector('.weight-designer .ds-canvas');
    const frameBottom = frame ? frame.getBoundingClientRect().bottom : window.innerHeight;
    return frameBottom - hgt - DOCK_PANE_GAP;
  };

  // ── PHONE: THE PICKER TAKES THE SHEET'S PLACE (Ryan 2026-07-28) ──────────────────────────────
  // "any time a colour menu opens, it should open over the bottom slide-up menu, and the slide-up
  // menu behind it should darken/blur and all its buttons become unclickable so long as the colour
  // menu is open. The weight preview pane should stay visible on top, so as you make adjustments you
  // can see them preview in real time on the 3D model."
  //
  // Which is the same argument docked mode makes on desktop, one axis over. Anchored to its swatch,
  // the picker floats wherever that swatch happens to be — often over the plate, which is the one
  // thing a colour picker must not cover. Pinned to the bottom it lands on the sheet instead, and the
  // sheet is the part of the screen whose job is already done: you are not choosing a swatch any
  // more, you are choosing a colour. The 3D keeps the whole band above.
  //
  // ⚠ `(max-width: 768px) and (pointer: coarse)`, not a width alone — the house phone rule. Ryan's
  // desktop is a TOUCHSCREEN and reports a coarse pointer, so a width-only test would put a docked
  // desktop picker into sheet mode the moment he narrowed a window, and a coarse-only test would do
  // it always. (`dockRect`'s own `innerWidth <= 760` is older and left alone; it only ever declines
  // to dock, which the float fallback handles.)
  const phoneMq = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(max-width: 768px) and (pointer: coarse)') : null;
  const isPhone = () => !!(phoneMq && phoneMq.matches);
  const sheetEl = () => document.querySelector('.weight-designer .ds-side-panel');
  /** Darken the sheet and take its taps away, for exactly as long as the picker is up. */
  function veilSheet(on) {
    const s = sheetEl();
    if (s) s.classList.toggle('is-cp-veiled', on);
  }
  /* ── DRAG THE HANDLE DOWN TO CLOSE ────────────────────────────────────────────────────────────
   * Deliberately simpler than the main sheet's drag, because the two gestures mean different things:
   * that one RESIZES between two snap points and has to track the finger to feel like a sheet; this
   * one has a single outcome, so it only has to be dismissible and to look like it is following.
   *   The card is translated with the finger — downward only, `Math.max(0, dy)`, so an upward drag
   * does nothing rather than lifting a sheet that has nowhere to go — and released past the
   * threshold it closes, below it springs back.
   *   ⚠ `close()` runs the SAME path as a light-dismiss, so an uncommitted preview is reverted by
   * the host's `onCancel` exactly as it is today. A gesture must not become a second way to commit.
   *   ⚠ TRANSFORM, NOT `bottom`. The card is pinned by its bottom edge with an inline value from the
   * pane's rect; animating that would fight `position()` on every resize tick. A transform is also
   * the only one of the two the compositor can take. */
  /* ⚠ THE SAME COMMIT-ON-SWIPE THE MAIN SHEET NOW USES (Ryan 2026-08-09: *"add the same feature to
     the colors pop-up/slide-up/down menus on mobile"*), and it arrives here for the same reason it
     arrived there: a gesture that DRIVES the card frame by frame is a gesture that repaints it,
     while a gesture that merely COMMITS hands the motion to a CSS transition the compositor runs.
     Past this many pixels downward the card is dismissed and the stylesheet's resting
     `translateY(100%)` does the rest.
       ⚠ DOWNWARD ONLY, unlike the sheet — this card has no second state to swipe TO. Up is not "open
     it further", it is nothing, so an upward swipe leaves it alone rather than committing something.
       Same 12px as the sheet, and load-bearing for the same reason: it is the line between brushing
     the handle and dismissing an edit. A TAP on the handle still does nothing at all — a picker that
     closed on an accidental brush would lose an in-flight colour. */
  const DRAG_COMMIT_PX = 12;
  /* ⚠ MUST OUTLAST the transform transition on `.wd-cp--sheet` in the stylesheet, or the card is
     torn out of the DOM part-way down. Erring long is the safe direction: the extra time is spent on
     an off-screen node with its listeners already removed. */
  const SHEET_CLOSE_MS = 600;
  let detachTimer = null;
  let dragY = null;
  sheetHead.addEventListener('pointerdown', (e) => {
    if (!el.classList.contains('wd-cp--sheet')) return;
    // The ✕ lives in this bar and owns its own click; a press on it is not the start of a swipe.
    if (e.target.closest && e.target.closest('.wd-cp__x')) return;
    dragY = e.clientY;
    try { sheetHead.setPointerCapture(e.pointerId); } catch { /* not capturable — the swipe still works */ }
  });
  sheetHead.addEventListener('pointermove', (e) => {
    if (dragY == null) return;
    if (e.clientY - dragY < DRAG_COMMIT_PX) return;   // still a tap, or heading the wrong way
    dragY = null;                                     // spent — the rest of the travel is inert
    try { sheetHead.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    /* `close()` runs the SAME path as the ✕ and a light-dismiss, so an uncommitted preview is
       reverted by the host's `onCancel` exactly as it is otherwise. A gesture must never become a
       second way to COMMIT. */
    close();
  });
  const endGrab = () => { dragY = null; };
  sheetHead.addEventListener('pointerup', endGrab);
  sheetHead.addEventListener('pointercancel', endGrab);

  function position() {
    if (!anchorEl) return;
    // Sheet mode owns its own geometry, in CSS — the inline left/top/width the other two modes write
    // have to be cleared or they would win over it.
    if (isPhone()) {
      el.classList.remove('wd-cp--docked');
      el.classList.add('wd-cp--sheet');
      // ── IT TAKES THE SHEET'S EXACT FOOTPRINT (Ryan 2026-07-28) ──────────────────────────────
      // "have the colour pop-up fill to the size of the slide-up pane behind it. Unless it needs more
      // room, then have it overlap and be bigger… if it's a little smaller, simply grow the colour
      // selector so the entire card fits the pane behind it. This means you don't need to blur or
      // darken the pane, because it'll be perfectly covered anyway."
      //   Which is a better idea than the veil it replaces: a dimmed pane is an explanation for why
      // you can see something you cannot touch, and the cleanest version of that explanation is not
      // being able to see it at all.
      //   Pinned by the BOTTOM and given the sheet's height as a MINIMUM, never as a fixed height.
      // That is what makes "unless it needs more room" free: the card is a flex column, the gradient
      // takes the slack when there is any, and when the content is taller than the sheet the top
      // simply rises past it. Nothing has to measure which case it is in.
      const r = sheetEl() && sheetEl().getBoundingClientRect();
      if (r && r.height > 0) {
        el.style.left = `${r.left}px`;
        el.style.width = `${r.width}px`;
        el.style.top = 'auto';
        el.style.bottom = `${Math.max(0, window.innerHeight - r.bottom)}px`;
        el.style.minHeight = `${r.height}px`;
        /* ⛔ …AND A CEILING TO GO WITH THE FLOOR (Ryan 2026-08-08) ─────────────────────────────
         * *"they used to be the perfect size until we added the ability to choose different
         * swatches… or until we added the ability to adjust line thickness, which got added to the
         * top as well… we need the menu to stay as tall as it normally is, and allow for scrolling
         * so the menu doesn't blow way up and obscure the view of the 3D model."*
         *
         * The min-height above bought "unless it needs more room, then have it overlap and be
         * bigger" (2026-07-28) — and that clause has been overtaken by its own successes. The swap
         * row and the thickness dial are BOTH new since, both stack on top, and together they push
         * the card past the pane and up over the model the picker exists to let you watch.
         *   Same number as the floor, so the card is now exactly the sheet's footprint: the covering
         * argument the veil was dropped for still holds, and the overflow goes where it belongs —
         * inside, on a scroll, rather than upward onto the 3D. The max-height IS the main sheet's,
         * because it is measured FROM the main sheet rather than restated.
         *   Cleared alongside `minHeight` on every path below, or a desktop resize would keep it. */
        el.style.maxHeight = `${r.height}px`;
      } else {
        // No sheet on screen (the picker can be opened from the gate) — fall back to the bottom inset
        // the stylesheet describes, and let the card be its natural height.
        el.style.left = ''; el.style.width = ''; el.style.top = ''; el.style.bottom = '';
        el.style.minHeight = ''; el.style.maxHeight = '';
      }
      return;
    }
    el.style.bottom = '';
    el.style.minHeight = '';
    el.style.maxHeight = '';
    el.classList.remove('wd-cp--sheet');
    const a = anchorEl.getBoundingClientRect();
    const dock = dockRect();
    if (dock) {
      el.classList.add('wd-cp--docked');
      el.style.width = dock.width + 'px';
      const hgt = el.offsetHeight || 300; // measure AFTER the width is set
      // Vertically CENTRED, every time (Ryan 2026-07-24) — it no longer tracks the anchor swatch.
      // Docked mode is a side panel, not a tooltip: it gave up proximity to the swatch the moment it
      // started docking to the pane edge, so following the anchor bought nothing and cost
      // predictability — clicking "Plate color" vs the last row threw it the height of the panel.
      // It was also only half-honouring the anchor anyway: the old clamp pinned it flat against the
      // viewport edge for any row near the top or bottom. A fixed position means the eye always knows
      // where the picker will appear; WHICH swatch is being edited is the target-highlight's job
      // (picker-UX overhaul #3), not physical proximity. `a` is still used by the float fallback.
      // BOTTOM-aligned, not centred (Ryan 2026-07-25). The canvas top-left now holds the Save/Share
      // cluster and its slide-down cards; a centred picker sat right on them. Dropping it to the
      // bottom of the same column keeps the shared left edge — the two read as one stack — and
      // leaves the room ABOVE free, so a card can slide out while the picker is open.
      //   On a short viewport there may not be enough height for both; they are simply allowed to
      // overlap then (the picker's z-index wins), which Ryan preferred to moving either one.
      el.style.left = dock.left + 'px';
      el.style.top = stableTop(hgt) + 'px';
      return;
    }
    el.classList.remove('wd-cp--docked');
    el.style.width = '';
    const w = el.offsetWidth || 232, hgt = el.offsetHeight || 300;
    let left = a.left;
    // keep inside the viewport
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    // On DESKTOP the float fallback takes the same fixed vertical position as the docked mode — the
    // whole point of `stableTop` is that WHICH mode you got, and which swatch you clicked, can't
    // move it. On a phone the picker still hangs off its swatch (with the flip-above), because
    // there it is a small popover on a small screen, not a side panel.
    let top = window.innerWidth > 760
      ? stableTop(hgt)
      : (a.bottom + 8 + hgt > window.innerHeight - 8 ? Math.max(8, a.top - hgt - 8) : a.bottom + 8);
    el.style.left = left + 'px';
    el.style.top = top + 'px';
  }
  const onDocDown = (e) => {
    if (el.contains(e.target)) return;
    /* ⛔ SHEET MODE DOES NOT LIGHT-DISMISS (Ryan 2026-08-09: *"sometimes you want the menu open and
       you want to be able to click and pinch or drag with your fingers to see the weight you're
       editing from a different angle as you edit the colors"*).
         On a phone "outside the picker" is not a neutral backdrop. The card takes the sheet's exact
       footprint, so everything still visible IS the 3D model — which means the one gesture a buyer
       most wants while choosing a colour was also the gesture that threw the colour away. The pane
       behind is `pointer-events: none` while veiled, so a tap that reaches anything at all reaches
       the plate, which is exactly where it should go.
         The ways out become the confirm button, the ✕ in the header, the drag, and Escape — and the
       ✕ exists BECAUSE of this line; see `closeX`. */
    if (el.classList.contains('wd-cp--sheet')) return;
    if (e.target !== anchorEl && !anchorEl.contains(e.target)) close();
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };
  /* ⛔ A SCROLL INSIDE THE CARD IS NOT A REFLOW (Ryan 2026-08-09: *"you need to press and swipe up to
   * move down the menu to read what's in it down below — as soon as you click and drag, it closes"*).
   *
   * 🔑 This listener is registered in the CAPTURE phase on `window`, and a `scroll` event — which does
   * NOT bubble — is still delivered down the capture path from any scrolling descendant. So the card's
   * own overflow was reporting itself as "the page moved under me". Harmless for as long as nothing
   * inside the picker scrolled; the max-height added in 248 turned "scroll down to read the rest of
   * it" into "dismiss it", one ask after the `touch-action` fix that made the scroll possible at all.
   * Same family, same cause: a rule that was correct while the card was a fixed-size popover.
   *
   * ⚠ AND A RESIZE MUST NOT DISMISS THE SHEET EITHER. On a phone the address bar collapsing, the
   * keyboard opening and a rotation all fire `resize`, and sheet geometry simply FOLLOWS the pane —
   * so it re-measures instead of giving up. Only the anchored desktop modes still dismiss, and they
   * must: their position is a one-shot computation against a scroll offset (see `stableTop`). */
  const onReflow = (e) => {
    if (!opened) return;
    const t = e && e.target;
    if (t && t.nodeType === 1 && el.contains(t)) return;
    if (el.classList.contains('wd-cp--sheet')) { position(); return; }
    close();
  };

  /**
   * @param {HTMLElement} anchor
   * @param {HTMLElement} mount
   * @param {object} [opts]
   * @param {Array<{key:string,label:string}>} [opts.assign] - show the "Apply to" footer
   *        with one button per slot; tapping one calls onAssign(key, currentHex()) + closes.
   * @param {{label?:string,onConfirm:(hex:string)=>void,onCancel?:()=>void,keepOpen?:boolean,
   *          closeLabel?:string}} [opts.confirm]
   *        - CONFIRM mode: show a single primary button (default "Add color"). Tapping it
   *        calls onConfirm(currentHex()) + closes; a light-dismiss / Escape calls onCancel().
   *        `keepOpen: true` commits WITHOUT dismissing and adds a secondary Close button — for a
   *        picker with target tabs, where finishing one colour must not throw away the strip that
   *        reaches the others. The host's onConfirm is then called once per commit, and onCancel
   *        still fires on the eventual dismiss for anything left uncommitted.
   * @param {Array<{key:string,label:string,hex?:string}>} [opts.targets] - 2+ things this open could
   *        edit → a tab strip at the top to choose between them (the outline rows' Inner/Middle/
   *        Outer/Added). Fewer than 2 renders nothing.
   * @param {string} [opts.activeTargetKey] - which target starts lit (default: the last one).
   * @param {string} [opts.targetsLabel] - caption above the tab strip. Supply it wherever the strip's
   *        meaning is not self-evident from the tab names — see the note on `targetLabel`.
   * @param {'label'|'swatch'} [opts.targetStyle] - 'swatch' renders each tab as a colour-filled
   *        `.wd-cchip` with no visible text (the label moves to the aria-label). For targets whose
   *        identity IS their colour — a logo's extracted palette — where a name would be a number.
   * @param {(key:string)=>void} [opts.onTargetChange] - a tab was picked; the HOST must re-point what
   *        its confirm handler writes to. The picker reseeds its own colour from that target's `hex`.
   */
  function open(anchor, mount, opts = {}) {
    anchorEl = anchor;
    // (Re)build the TARGET tabs for this open. Only when there are 2+ — one target needs no chooser.
    // A tab click reseeds the picker to THAT target's current colour and tells the host to re-point
    // its confirm handler, so "Update color" always lands on the ring whose tab is lit.
    targetRow.textContent = '';
    targetButtons = [];
    const targets = Array.isArray(opts.targets) ? opts.targets.filter((t) => t && t.key) : [];
    if (targets.length > 1) {
      targetBlock.hidden = false;
      targetLabel.textContent = typeof opts.targetsLabel === 'string' ? opts.targetsLabel : '';
      targetLabel.hidden = !targetLabel.textContent;
      targetRow.setAttribute('role', 'tablist');
      let activeKey = opts.activeTargetKey || targets[targets.length - 1].key;
      const paintTabs = () => targetButtons.forEach(({ btn, key }) => {
        const on = key === activeKey;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      // ── TWO SHAPES OF TAB, BY WHAT THE TARGET IS ────────────────────────────────────────────
      // The outline rows name their targets — Inner, Middle, Outer, Added — so those tabs are a word
      // with a colour dot beside it, and the word is the point. A logo's extracted palette has no
      // names to give: the colour IS the identity, and a numbered label is just a handle for it
      // (Ryan 2026-07-28: "have the entire button be glass-like and filled with the colour, just like
      // regular square swatches are").
      //   So swatch mode drops the label into the aria-label and wears `.wd-cchip` — the actual
      // colour-swatch class, not a copy of its values. It picks up the rounded-square glass, the lit
      // top edge, the sheen, and — because `paintTabs` sets `is-active` — the same white selection
      // ring every other selected swatch in the tool wears. Sharing the class is what stops these
      // drifting from the swatch rows they are imitating.
      const swatchTabs = opts.targetStyle === 'swatch';
      targets.forEach((t) => {
        const btn = h('button', {
          type: 'button', role: 'tab',
          class: `wd-cp__target${swatchTabs ? ' wd-cp__target--swatch wd-cchip' : ''}`,
          // A named target reads "Edit the Inner (#fff)"; a swatch's label is a bare handle, so the
          // same template gave "Edit the 1 (#FFD200)". Match the aria-label instead of inventing a
          // third wording.
          title: swatchTabs ? `Edit color ${t.label}${t.hex ? ` (${t.hex})` : ''}` : `Edit the ${t.label}${t.hex ? ` (${t.hex})` : ''}`,
          'aria-label': swatchTabs ? `Edit color ${t.label}${t.hex ? ` (${t.hex})` : ''}` : null,
          style: swatchTabs && t.hex ? { background: t.hex } : null,
          onClick: (e) => {
            e.stopPropagation();
            if (activeKey === t.key) return;
            activeKey = t.key;
            paintTabs();
            if (typeof opts.onTargetChange === 'function') opts.onTargetChange(t.key);
            // A different ring has a different thickness, so the baseline moves with the tab — else
            // the gate would compare this ring's dial against the last one's number.
            openedExtra = readExtra();
            if (t.hex) setColor(t.hex); // reseed to what that ring currently IS
            /* ⛔ AND THE COLOUR BASELINE MOVES WITH IT, FOR THE IDENTICAL REASON (2026-08-15). Only
               `openedExtra` was being re-taken, so after a tab switch `syncConfirmEnabled` compared
               THIS target's colour against the one the popover opened on: two targets of different
               colours — which is every swatch strip — lit "Update color" before anything had been
               edited. Pressing it was harmless (`to === from` short-circuits the recolour), but an
               enabled button that does nothing is the affordance lying about what it will do.
               ⚠ After `setColor`, which is what `currentHex()` reads. */
            openedHex = currentHex();
            syncConfirmEnabled();
          },
        },
        swatchTabs ? null : h('span', { class: 'wd-cp__target-dot', style: { background: t.hex || 'transparent' } }),
        swatchTabs ? null : t.label);
        targetButtons.push({ btn, key: t.key });
        targetRow.append(btn);
      });
      paintTabs();
    } else {
      targetBlock.hidden = true;
      targetRow.removeAttribute('role');
    }
    // The host's extra control (outline thickness). Mounted, not built, here — see `extraRow`.
    extraRow.textContent = '';
    if (opts.extra instanceof Node) { extraRow.hidden = false; extraRow.append(opts.extra); }
    else extraRow.hidden = true;
    // (Re)build the footer for this open. Three modes: CONFIRM ("Add color" — craft then
    // commit), ASSIGN ("Apply to" slot buttons), or a plain direct-edit (footer hidden).
    assignRow.textContent = '';
    assignButtons = [];
    activeConfirm = null;

    /* (Re)build the SWAP row. Hidden unless the host offers a pool AND this open can commit — a
       one-click swap with nowhere to land would be a button that silently does nothing. Each chip is
       a real `.wd-cchip`, the same class every other colour swatch in the tool wears, so it inherits
       the glass, the lit edge and the selection ring rather than re-implementing them (the rule the
       target tabs already follow). The colour currently in the slot is marked `is-active` and does
       not commit on click — it is the "you are here", not a no-op swap. */
    // Set the moment a chip is tapped; decides what "Update color" means and whether a dismiss has
    // a live preview to undo. Per-open, so it can never leak between two visits to the editor.
    let swapPreviewed = false;
    openedHex = currentHex();   // the baseline "nothing has changed yet" for the confirm gate
    activeExtraValue = typeof opts.extraValue === 'function' ? opts.extraValue : null;
    openedExtra = readExtra();  // …and the same baseline for whatever the host put in `extra`

    /* ONE COMMIT PATH, shared by the confirm button and the swap chips. Reads `activeConfirm` at
       CLICK time, not build time — the swap row is constructed before the confirm branch runs. */
    const keepOpenMode = !!(opts.confirm && opts.confirm.keepOpen === true);
    const commitCurrent = (meta) => {
      /* ⚠ ONCE A CHIP HAS BEEN TAPPED, "Update color" MEANS THE SWAP. The confirm handler's own verb
         is `recolorEverywhere` — change this colour wherever the design uses it — which is right for
         a colour you DIALLED and wrong for one you picked out of the design's existing set (it is
         what deleted the colour you swapped away from, changelog 237). The preview has already
         re-pointed the slot, so confirming just keeps it. */
      if (swapPreviewed && onPick) {
        onPick(currentHex());
        swapPreviewed = false;
        activeConfirm = null;   // committed — a later dismiss must not also fire onCancel/revert
        close();
        return;
      }
      if (!activeConfirm) return;
      if (keepOpenMode) {
        activeConfirm.onConfirm(currentHex(), meta);
        const live = targetButtons.find(({ btn: b }) => b.classList.contains('is-active'));
        if (live) live.btn.style.background = currentHex();
        openedHex = currentHex();   // committed — nothing to apply again until the next edit
        openedExtra = readExtra();  // …and the thickness they just committed is the new baseline
        syncConfirmEnabled();
        return;
      }
      const c = activeConfirm; activeConfirm = null;
      c.onConfirm(currentHex(), meta);
      close();
    };

    swapRow.textContent = '';
    const swap = opts.swap && Array.isArray(opts.swap.colors) ? opts.swap.colors : [];
    /* ⛔ A SWAP IS NOT "UPDATE COLOR" (Ryan 2026-08-05, second pass). The first cut routed the chips
       through `commitCurrent`, i.e. the confirm button's handler — which in the ✎ editor is
       `recolorEverywhere(from, to)`. That is the right meaning for the BUTTON ("change this colour
       everywhere it is used") and the wrong one entirely for a chip: it rewrote the palette role and
       the swatch pool (deduping the old colour out of existence, so you could not swap back — the
       symptom Ryan reported) and recorded a `logoToneSwaps` entry, which repaints the LOGO'S OWN
       pixels. Swapping a ring to blue should not turn the artwork's grey to blue.
         So a swap gets its OWN action. The host supplies `onPick`, which re-points just the slot
       being edited; no `onPick`, no row, because a chip that silently does the wrong thing is worse
       than no chip. */
    const onPick = opts.swap && typeof opts.swap.onPick === 'function' ? opts.swap.onPick : null;
    const onPreview = opts.swap && typeof opts.swap.onPreview === 'function' ? opts.swap.onPreview : null;
    const swapChips = [];
    const syncSwapActive = (activeHex) => {
      const want = String(activeHex || '').toUpperCase();
      for (const { btn, hex } of swapChips) {
        const on = hex === want;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-label', on ? `${btn.dataset.label}, already applied` : `Use ${btn.dataset.label}`);
        btn.title = on ? `${btn.dataset.label} — already applied` : `Use ${btn.dataset.label}`;
      }
    };
    /* ⛔ A ROW WHOSE ONLY CHIP IS THE ONE ALREADY APPLIED IS A CONTROL WITH NO ACTION (Ryan
       2026-08-05). Once the host filters to colours that actually READ in this slot, a three-colour
       palette can easily leave exactly one survivor — the current one — and rendering it alone offers
       a tap that does nothing. Hide the row instead; the picker below still reaches every colour. */
    const swappable = swap.filter((c) => String((c && c.hex) || '').toUpperCase() !== String(currentHex() || '').toUpperCase());
    if (swap.length && swappable.length && onPick) {
      swapBlock.hidden = false;
      swapRow.setAttribute('role', 'group');
      swapRow.setAttribute('aria-label', 'Swap in a colour this design already uses');
      for (const c of swap) {
        const hex = String((c && c.hex) || '').toUpperCase();
        if (!/^#[0-9A-F]{6}$/.test(hex)) continue;
        const label = (c && c.label) || hex;
        const btn = h('button', {
          type: 'button', class: 'wd-cchip wd-cp__swap-chip', style: { background: hex },
          onClick: (e) => {
            e.stopPropagation();
            if (btn.classList.contains('is-active')) return; // the "you are here" chip is inert
            /* ⚠ A CHIP PREVIEWS, IT DOES NOT COMMIT (Ryan 2026-08-05): *"merely selecting the swatch
               should preview it on the 3D model and the pop-up should stay popped up. Then to
               confirm the new color, you have to click Update color to lock it in and close."*
                 The first cut applied on tap, and because the host commits structurally — which
               rebuilds the design panel and takes this popover's anchor with it — the window shut on
               every tap. You could not compare two colours without reopening the editor twice. */
            setColor(hex);          // the dial follows, so the popover stays honest
            swapPreviewed = true;   // this open is now a SWAP, not a recolour-everywhere
            if (onPreview) onPreview(hex);
            syncSwapActive(hex);
            syncConfirmEnabled();
          },
        });
        btn.dataset.label = label;
        swapChips.push({ btn, hex });
        swapRow.append(btn);
      }
      syncSwapActive(currentHex());
    } else {
      swapBlock.hidden = true;
    }
    const slots = Array.isArray(opts.assign) ? opts.assign : null;
    if (opts.confirm && typeof opts.confirm.onConfirm === 'function') {
      activeConfirm = opts.confirm;
      assignRow.hidden = false;
      // ── KEEP-OPEN MODE (Ryan 2026-07-28) ──────────────────────────────────────────────────────
      // "add a Close button next to Update color… this allows someone to update the colour, then
      // switch to another swatch at the top and change that one up too."
      //
      // Confirm has always meant commit-AND-dismiss, which is right where there is one thing to edit
      // — the CD tab's fill and outline editors, "Add color" — and wrong the moment the popover holds
      // a tab per colour: finishing one would throw away the strip that exists to reach the others.
      // So it is OPT-IN. Every existing caller keeps the contract it was written against; only a
      // picker that offers a choice of targets asks to survive the choice being taken.
      //
      // Two consequences, both easy to get wrong:
      //  · `activeConfirm` is NOT cleared on commit here. It is what makes a later light-dismiss or
      //    Escape call `onCancel` — and after one committed edit, a second uncommitted one is exactly
      //    the state that still needs reverting.
      //  · the ACTIVE TAB is repainted to the colour just committed. Its swatch was filled from the
      //    hex at open, so leaving it would show the old colour under the ring — the picker telling
      //    the buyer their change had not taken.
      const keepOpen = opts.confirm.keepOpen === true;
      const btn = h('button', {
        type: 'button', class: 'wd-cp__confirm-btn wd-flat-glass wd-flat-glass--flush',
        onClick: (e) => { e.stopPropagation(); commitCurrent(); },
      }, opts.confirm.label || 'Add color');
      assignRow.append(btn);
      confirmBtn = btn;
      syncConfirmEnabled();
      if (keepOpen) {
        // Secondary by design — smaller, quieter, and second in the row. "Update color" is the verb
        // this popover exists for; Close is the way out, not the outcome.
        assignRow.append(h('button', {
          type: 'button', class: 'wd-cp__close-btn wd-flat-glass wd-flat-glass--flush',
          onClick: (e) => { e.stopPropagation(); close(); },
        }, opts.confirm.closeLabel || 'Close'));
      }
    } else if (slots && slots.length) {
      assignRow.hidden = false;
      const btnRow = h('div', { class: 'wd-cp__assign-btns' });
      slots.forEach((s) => {
        const btn = h('button', {
          type: 'button', class: 'wd-cp__assign-btn',
          onClick: (e) => { e.stopPropagation(); if (btn.disabled) return; if (typeof onAssign === 'function') onAssign(s.key, currentHex()); close(); },
        }, s.label);
        assignButtons.push({ btn, disabled: typeof s.disabled === 'function' ? s.disabled : null });
        btnRow.append(btn);
      });
      assignRow.append(h('span', { class: 'wd-cp__assign-label' }, 'Apply to'), btnRow);
      updateAssignStates(); // initial state matches the seeded color
    } else {
      assignRow.hidden = true;
    }
    // Re-home on EVERY open if the requested mount changed or the old parent went away. The old
    // `if (!el.parentNode)` only ever mounted once: a torn-down parent leaves `parentNode` pointing
    // at the DETACHED node (it is not null), so the picker would stay off-document and never show
    // again. That was harmless while the mount was the always-present designer root, but the mount
    // is now `.ds-canvas` — which must be the picker's parent for a Save/Share card to paint over it
    // (see `pickerMount()` in ui/design-panel.js) and which host code may rebuild.
    const target = mount || document.body;
    // A dismissal now leaves the node in place for the length of its slide-out (see `close`). Catch
    // it mid-flight rather than letting the pending removal fire under a picker that is open again.
    if (detachTimer) { clearTimeout(detachTimer); detachTimer = null; }
    el.style.pointerEvents = '';   // a re-open mid-slide catches a card that was made inert
    if (el.parentNode !== target || !el.isConnected) target.appendChild(el);
    opened = true;
    // Position (which may add wd-cp--docked + set the width) BEFORE is-open, then flush a
    // reflow, so the open transition animates from the docked start-transform (slide out of the
    // pane) instead of the default drop (Ryan 2026-07-23).
    position();
    // Veiled from `position`'s own verdict, not a second reading of the media query — one decision
    // about whether this is sheet mode, so the pane can never be dimmed by a picker that floated.
    veilSheet(el.classList.contains('wd-cp--sheet'));
    void el.offsetWidth;
    el.classList.add('is-open');
    // defer so the opening click doesn't immediately light-dismiss it
    setTimeout(() => {
      document.addEventListener('pointerdown', onDocDown, true);
      document.addEventListener('keydown', onKey, true);
      window.addEventListener('resize', onReflow, true);
      window.addEventListener('scroll', onReflow, true);
    }, 0);
  }
  /** Teardown that has to wait for the slide-out. Everything in here is safe to run late: it is the
   *  node's own geometry and its place in the DOM, nothing the host or the design can observe. */
  function detach() {
    detachTimer = null;
    el.classList.remove('wd-cp--sheet');
    el.style.width = '';
    el.style.transform = '';
    el.style.transition = '';
    el.style.pointerEvents = '';
    if (el.parentNode) el.parentNode.removeChild(el);
  }
  function close() {
    if (!opened) return;
    opened = false;
    /* Read BEFORE the classes come off — it decides whether this dismissal animates. */
    const sliding = el.classList.contains('wd-cp--sheet');
    el.classList.remove('is-open');
    el.classList.remove('wd-cp--docked');
    // ⚠ UNCONDITIONALLY, not `if (isPhone())`. A device can stop matching the phone query between
    // open and close — a rotation, a resized window, an external keyboard changing the pointer type —
    // and a sheet left veiled is a pane the buyer can see and cannot touch, with nothing on screen
    // explaining why. Removing a class that is not there costs nothing.
    veilSheet(false);
    document.removeEventListener('pointerdown', onDocDown, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', onReflow, true);
    window.removeEventListener('scroll', onReflow, true);
    /* ── IT LEAVES THE WAY IT ARRIVED (Ryan 2026-08-09) ──────────────────────────────────────────
     * The slide-up he asked for only teaches the gesture if the card also goes back down the way it
     * came; a sheet that rises smoothly and then blinks out of existence teaches nothing, and after
     * a drag-to-close it was worse than nothing — the card snapped back UP to its resting place and
     * only then vanished.
     *   So the node leaves LAST, after the transform transition has run. Dropping the inline
     * transform here is what starts it: with `is-open` gone the resting rule is `translateY(100%)`,
     * and the card eases there from where it sits. (It clears an inline transform that nothing sets
     * any more — the swipe COMMITS rather than dragging the card — but leaving the line costs
     * nothing and keeps this the one place that hands the card back to the stylesheet.)
     *   ⚠ Everything ABOVE this point still happens synchronously, and that is the whole safety
     * argument: `opened` is already false, the listeners are already gone, and `onCancel` below still
     * reverts the preview on this tick. Only the node's removal is late. `open()` cancels the timer,
     * so a re-open during the slide catches the same element and lifts it again. */
    if (sliding) {
      /* ⛔ INERT THE INSTANT IT IS DISMISSED, NOT WHEN IT LEAVES (Ryan 2026-08-09: *"whenever the
         color slide-up menu closes, the first next swipe on the menu underneath does nothing — but
         the second one works. The first click will work however, just not the first scroll"*).
           That sentence names the mechanism exactly. For the length of the slide the card is still a
         `position: fixed` box over the sheet, and it is still SCROLLABLE — so a swipe started in
         that window is claimed by the departing card's own overflow and the pane underneath never
         sees it. A tap is unaffected because a tap on the card does nothing visible and reads as
         "worked"; only a gesture has somewhere else it was supposed to go.
           This is the cost of animating the exit rather than yanking the node, and `pointer-events`
         is the whole payment: out of hit-testing means the card's `overflow` and the header's
         `touch-action: none` stop applying too, so the first gesture lands where it should. */
      el.style.pointerEvents = 'none';
      el.style.transition = '';
      el.style.transform = '';
      if (detachTimer) clearTimeout(detachTimer);
      detachTimer = setTimeout(detach, SHEET_CLOSE_MS);
    } else {
      detach();
    }
    // Confirm mode closed WITHOUT the button → cancel (revert the live preview).
    const c = activeConfirm; activeConfirm = null;
    if (c && typeof c.onCancel === 'function') c.onCancel();
    if (typeof onClose === 'function') onClose();
  }
  function setColor(hex) { const { r, g, b } = hexToRgb(hex); hsv = rgbToHsv(r, g, b); paint(); }

  return { el, open, close, setColor, syncExtra, isOpen: () => opened };
}
